import React, { FC, Fragment, useEffect, useState } from 'react';
import { Badge, Box, Tab } from '@mui/material';
import { useQuery } from '@apollo/client';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useParams, useNavigate } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import ClientDirectory from 'pages/client-directory/clients/client-directory.component';
import DentistDirectory from 'pages/client-directory/dentists/dentist-directory.component';
import IncomingSupplierRequests from 'components/supplier-requests/incoming-supplier-requests.component';
import CustomerBrowser from 'components/customer-browser/customer-browser.component';
import { incomingSupplierRequestsTemplate } from 'apollo/templates/supplier-request';
import { SupplierRequestStatus } from 'models/supplier-request';
import ClinicRequests from 'components/clinic-requests/clinic-requests.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { incomingClinicRequestsTemplate } from 'apollo/templates/clinic-request';
import { ClinicRequestStatus } from 'models/clinic-request';
import PriceRequests from 'pages/price-requests/price-requests.component';

const DirectoryTab: FC = () => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'organization', 'orders']);
    const currentUser = currentUserVar();
    const { tab } = useParams<{ tab: string; id: string }>();
    const [tabState, setTabState] = useState(tab || 'clients');
    const navigate = useNavigate();
    // DD-3662: the number of requests waiting for an answer, on the tab.
    const { data: pendingRequests } = useQuery(incomingSupplierRequestsTemplate, {
        variables: { status: SupplierRequestStatus.PENDING },
        fetchPolicy: 'cache-and-network',
        skip: !permissions.isLabTechnician(currentUser.type),
    });
    // DD-3702: clinics' requests to the lab count too — the tab holds both kinds.
    const { data: pendingClinicRequests } = useQuery(incomingClinicRequestsTemplate, {
        variables: { status: ClinicRequestStatus.PENDING },
        fetchPolicy: 'cache-and-network',
        skip: !permissions.isLabTechnician(currentUser.type),
    });
    const pendingCount: number =
        (pendingRequests?.incomingSupplierRequests?.length ?? 0) +
        (pendingClinicRequests?.incomingClinicRequests?.length ?? 0);
    // DD-3698: browsing for customers is the lab's own business; the backend refuses external users too.
    const canBrowseCustomers = !permissions.isExternalTechnician(currentUser?.role);
    // DD-3840: price requests (DD-3697) live here for labs; the backend limits them to lab admins.
    const canSeePriceAdjustments = permissions.isTechnicianAdmin(currentUser?.role);

    useEffect(() => {
        handleTabChange(undefined, tab || 'clients');
    }, [tab]);

    const handleTabChange = (_: unknown, newTabState: string) => {
        const routeMap: { [key: string]: string } = {
            clients: 'clients',
            dentists: 'dentists',
            'supplier-requests': 'supplier-requests',
            'add-customer': 'add-customer',
            'price-adjustments': 'price-adjustments',
        };
        if (tabState !== newTabState) {
            const url = `/client-directory/${routeMap[newTabState]}`;
            navigate(url);
            setTabState(newTabState);
        }
    };

    return (
        <Fragment>
            {permissions.isLabTechnician(currentUser.type) ? (
                <Fragment>
                    <TabContext value={tabState}>
                        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                            <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                                <Tab label={translate('client-directory:CLIENTS')} value="clients" />
                                <Tab label={translate('common:DENTISTS')} value="dentists" />
                                {canBrowseCustomers ? (
                                    <Tab
                                        label={translate('organization:ADD_CUSTOMER')}
                                        value="add-customer"
                                        data-test="client-directory.add-customer-tab"
                                    />
                                ) : undefined}
                                <Tab
                                    value="supplier-requests"
                                    data-test="client-directory.supplier-requests-tab"
                                    label={
                                        <Badge
                                            color="primary"
                                            badgeContent={pendingCount}
                                            sx={{ pr: pendingCount ? 1.5 : 0 }}
                                        >
                                            {translate('client-directory:REQUESTS')}
                                        </Badge>
                                    }
                                />
                                {canSeePriceAdjustments ? (
                                    <Tab
                                        label={translate('client-directory:PRICE_ADJUSTMENTS')}
                                        value="price-adjustments"
                                        data-test="client-directory.price-adjustments-tab"
                                    />
                                ) : undefined}
                            </TabList>
                        </Box>
                        <TabPanel sx={{ padding: 0 }} value="clients">
                            <ClientDirectory />
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="dentists">
                            <DentistDirectory />
                        </TabPanel>
                        {canBrowseCustomers ? (
                            <TabPanel sx={{ padding: 0 }} value="add-customer">
                                <CustomerBrowser />
                            </TabPanel>
                        ) : undefined}
                        {/* DD-3702: one Requests tab — labs asking to buy from this lab, and the
                            lab's requests to and from Denthub Pro clinics. */}
                        <TabPanel sx={{ padding: 0 }} value="supplier-requests">
                            <PageTitle headingStyle="h3" title={translate('client-directory:SUPPLIER_REQUESTS')} />
                            <IncomingSupplierRequests />
                            <Box mt={5}>
                                <ClinicRequests />
                            </Box>
                        </TabPanel>
                        {canSeePriceAdjustments ? (
                            <TabPanel sx={{ padding: 0 }} value="price-adjustments">
                                <PriceRequests />
                            </TabPanel>
                        ) : undefined}
                    </TabContext>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};
export default DirectoryTab;
